import React from 'react';
import { cn } from 'lib/utils';
import { Loader2, ShieldCheck } from 'lucide-react';
import { RemoteEhrStatus } from '../types';

interface RemoteSessionIndicatorProps {
  status: RemoteEhrStatus | null;
  isFetching: boolean;
  onOpenDetails?: () => void;
}

export const RemoteSessionIndicator: React.FC<RemoteSessionIndicatorProps> = ({
  status,
  isFetching,
  onOpenDetails,
}) => {
  const isWorking = status === 'authenticated' || isFetching;
  const isComplete = status === 'completed' && !isFetching;

  if (!isWorking && !isComplete) {
    return null;
  }

  const title = isComplete ? 'Journal imported' : 'Import in progress';
  const subtitle = isComplete
    ? 'Ready to explore your records.'
    : status === 'authenticated'
      ? 'Securely downloading from 1177.se…'
      : 'Finalizing your exports…';
  const ariaLabel = isComplete
    ? 'Journal imported successfully. Open to review the download details.'
    : 'Journal import in progress. Open to review the live status.';

  return (
    <button
      type="button"
      onClick={onOpenDetails}
      aria-label={ariaLabel}
      className={cn(
        'fixed inline-flex items-center gap-2 py-[0.55rem] px-4 rounded-full',
        'border border-primary/20 bg-card text-foreground-strong',
        'shadow-lg backdrop-blur-sm',
        'text-sm font-medium cursor-pointer z-indicator'
      )}
    >
      {isWorking && !isComplete ? (
        <Loader2 size={18} className="text-primary animate-spin" aria-hidden="true" />
      ) : (
        <ShieldCheck size={18} className="text-emerald-600" aria-hidden="true" />
      )}
      <span className="flex flex-col items-start gap-[0.1rem] leading-tight" aria-live="polite">
        <span className="font-semibold text-xs tracking-[0.01em]">{title}</span>
        <span className="text-xs text-foreground-muted">{subtitle}</span>
      </span>
    </button>
  );
};
